import DateFnsUtils from '@date-io/date-fns';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import UndoOutlinedIcon from '@material-ui/icons/UndoOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ErrorMessage, Formik } from 'formik';
import { cloneDeep, get } from 'lodash';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useAppContext } from '../../../config/AppContext';
import {
  getTimezoneDate,
  getUTCDate,
} from '../../../services/Util/Util.service';
import { PromotionType } from './PromotionType.enum';
import CategoryService from '../../../services/Category/Category.service';
import ProductService from '../../../services/Product/Product.service';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const PromoDialog = (props: any) => {
  const { promo, close, submit, promoCategory } = props;
  const { venue, stores } = useAppContext();
  const [categoriesObj, setCategoriesObj] = useState<{
    categories: any[];
    selected: any;
  }>({ categories: [], selected: '' });
  const [productsObj, setProductsObj] = useState<{
    products: any[];
    selected: any;
  }>({ products: [], selected: '' });
  const [selectedStoreId, setSelectedStoredId] = useState<string>('');
  const [searchProducts, setSearchProducts] = useState<string>('');

  const PromoValidationSchema = () => {
    return Yup.object().shape(
      {
        code: Yup.string().required('Code is a required field'),
        discountAmount: Yup.number().when(['discountPercentage'], {
          is: discountPercentage => discountPercentage > 0,
          otherwise: Yup.number()
            .min(0)
            .positive(
              'One of Discount Amount or Discount Percentage must be greater than 0',
            ),
          then: Yup.number()
            .min(0)
            .notRequired()
            .test(
              'one',
              'Only one of Discount Amount or Discount Percentage can be more than 0',
              function(value) {
                const { discountPercentage } = this.parent;
                return !(discountPercentage > 0 && value > 0);
              },
            ),
        }),
        discountPercentage: Yup.number().when(['discountAmount'], {
          is: discountAmount => discountAmount > 0,
          otherwise: Yup.number()
            .min(0)
            .positive(
              'One of Discount Amount or Discount Percentage must be greater than 0',
            ),
          then: Yup.number()
            .min(0)
            .notRequired()
            .test(
              'one',
              'Only one of Discount Amount or Discount Percentage can be more than 0',
              function(value) {
                const { discountAmount } = this.parent;
                return !(discountAmount > 0 && value > 0);
              },
            ),
        }),
        endDateTime: Yup.date()
          .required()
          .test('min', 'End Date should be greater than Start Date.', function(
            value,
          ) {
            const { startDateTime } = this.parent;
            return value > startDateTime;
          }),
        maxUsesPerUser: Yup.number()
          .min(1)
          .notRequired()
          .nullable(),

        productLimit: Yup.number()
          .min(1)
          .notRequired()
          .nullable(),

        minPurchaseAmount: Yup.number().when('type', {
          is: PromotionType.AUTOMATIC,
          otherwise: Yup.number()
            .min(0)
            .notRequired(),
          then: Yup.number()
            .min(0)
            .required('Minimum Purchase Amount is required.'),
        }),
        startDateTime: Yup.date().required(),
        storeIds: Yup.array().required('Please select at least one store.'),
        categoryId: Yup.string().when([], {
          is: () => promoCategory === 'PRODUCT',
          then: Yup.string().required(
            'Please select at least one category after selecting store',
          ),
          otherwise: Yup.string()
            .nullable()
            .notRequired(),
        }),
        product_id: Yup.string().when([], {
          is: () => promoCategory === 'PRODUCT',
          then: Yup.string().required('Please select at least one product'),
          otherwise: Yup.string()
            .nullable()
            .notRequired(),
        }),
        type: Yup.string()
          .oneOf(Object.keys(PromotionType))
          .required(),
      },
      [['discountAmount', 'discountPercentage']],
    );
  };

  useEffect(() => {
    if (promo?.storeIds && promoCategory == 'PRODUCT') {
      setSelectedStoredId(promo.storeIds[0]);
      setCategoriesObj({ ...categoriesObj, selected: promo.categoryId });
      setProductsObj({ ...productsObj, selected: promo.product_id });
    }
  }, [promo]);

  const fetchCategories = async () => {
    if (!selectedStoreId || selectedStoreId == 'All') {
      return;
    }
    try {
      const { data }: any = await CategoryService.getAllCategories(
        selectedStoreId,
        100,
        1,
      );
      setCategoriesObj({ ...categoriesObj, categories: data.items });
    } catch (err) {
      console.log(err, 'error while fetching categories');
    }
  };

  const fetchStoreProductsByCategory = async (categoryId: string) => {
    const rowsPerPage = 100;
    const page = 0;
    const store = selectedStoreId!;

    try {
      const response: any = await ProductService.getStoreProducts(
        store,
        rowsPerPage,
        page + 1,
        categoryId,
      );
      const categoryMeals = response.data.items
        ?.filter((product: any) => product.active === true)
        .map((product: any, index: any) => ({
          id: product.id,
          key: index,
          description: product.description,
          name: product.name,
          imageUrl: product.imageUrl
            ? product.imageUrl
            : product.globalProduct && product.globalProduct.imageUrl,
          price: product.price,
          value: (index + 1).toString(),
        }));
      setProductsObj({ ...productsObj, products: categoryMeals });
    } catch (error) {
      // console.log(error);
    }
  };

  const filteredProducts = productsObj.products.filter(product =>
    product.name.toLowerCase().includes(searchProducts.toLowerCase()),
  );

  useEffect(() => {
    if (promoCategory == 'PRODUCT') fetchCategories();
  }, [selectedStoreId]);

  useEffect(() => {
    if (categoriesObj.selected && promoCategory == 'PRODUCT') {
      fetchStoreProductsByCategory(categoriesObj.selected);
    }
  }, [categoriesObj.selected]);

  if (!venue) {
    return null;
  }

  const promoCopy = cloneDeep(promo);
  promoCopy.startDateTime = getTimezoneDate(
    new Date(promoCopy.startDateTime),
    get(venue, 'timezone', ''),
  );
  promoCopy.endDateTime = getTimezoneDate(
    new Date(promoCopy.endDateTime),
    get(venue, 'timezone', ''),
  );

  promoCopy.storeIds = stores.filter((f: any) => {
    return promoCopy.storeIds.some((v: any) => v === f.id);
  });
  console.log('Store IDs', promoCopy);
  let multiSelectStore = promoCategory == 'PRODUCT' ? false : true;

  return (
    <Dialog open={true} onClose={close} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title" className="section-title">
        <span className="section-title">
          {promoCopy.id
            ? `Edit ${promoCategory == 'ORDER' ? 'Order' : 'Product'}  Promo`
            : `Add ${promoCategory == 'ORDER' ? 'Order' : 'Product'} Promo`}
        </span>
      </DialogTitle>
      <DialogContent>
        <div className="generic-dialog">
          <Formik
            initialValues={promoCopy}
            onSubmit={values => {
              const {
                endDateTime,
                startDateTime,
                storeIds,
                type,
                discountAmount,
                discountPercentage,
                minPurchaseAmount,
                maxUsesPerUser,
                productLimit,
              } = values;
              console.log('Selected Product ID:', productsObj.selected);
              submit({
                ...values,
                promotionCategory: promoCategory,
                ...(promoCategory === 'PRODUCT' && {
                  product_id: productsObj.selected,
                  productLimit: productLimit,
                }),
                discountAmount: discountAmount || 0,
                discountPercentage: discountPercentage || 0,
                endDateTime:
                  typeof endDateTime === 'string'
                    ? endDateTime
                    : getUTCDate(
                        new Date(endDateTime),
                        get(venue, 'timezone', ''),
                      ).toISOString(),
                maxUsesPerUser: maxUsesPerUser || null,
                minPurchaseAmount: minPurchaseAmount || 0,
                startDateTime:
                  typeof startDateTime === 'string'
                    ? startDateTime
                    : getUTCDate(
                        new Date(startDateTime),
                        get(venue, 'timezone', ''),
                      ).toISOString(),
                storeIds: storeIds.map((s: any) => s.id),
              });
            }}
            validationSchema={PromoValidationSchema}
          >
            {({
              values,
              handleChange,
              handleSubmit,
              setFieldValue,
              setFieldError,
              handleBlur,
              errors,
              touched,
            }) => (
              <form onSubmit={handleSubmit}>
                <Autocomplete
                  multiple={multiSelectStore}
                  className="generic-dialog__field"
                  options={stores}
                  disableCloseOnSelect={true}
                  onChange={(event: any, newValue: any) => {
                    if (promoCategory == 'PRODUCT') {
                      if (newValue == null) {
                        setCategoriesObj({ ...categoriesObj, categories: [] });
                        setProductsObj({ ...productsObj, products: [] });
                      } else {
                        setSelectedStoredId(newValue.id);
                        if (selectedStoreId !== newValue.id) {
                          setProductsObj({ ...productsObj, products: [] });
                        }
                      }
                      setFieldValue('storeIds', [newValue]);
                    } else {
                      setFieldValue('storeIds', newValue);
                    }
                  }}
                  value={
                    promoCategory == 'PRODUCT'
                      ? values.storeIds[0]
                      : values.storeIds
                  }
                  noOptionsText="No Stores"
                  getOptionLabel={(option: any) => option.name}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </React.Fragment>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Stores"
                      placeholder="Select Stores"
                    />
                  )}
                />
                <ErrorMessage
                  className="error"
                  component="div"
                  name="storeIds"
                />
                {promoCategory == 'PRODUCT' && (
                  <>
                    <Grid container spacing={0} style={{ marginTop: '20px' }}>
                      <Grid item lg={6} md={6}>
                        <TextField
                          margin="dense"
                          placeholder="Search"
                          variant="outlined"
                          onChange={(event: any) => {
                            const {
                              target: { value },
                            } = event;
                            setSearchProducts(value);
                          }}
                          value={searchProducts}
                        />
                      </Grid>
                      <Grid item lg={6} md={6}>
                        <FormControl className="generic-dialog__field">
                          <InputLabel
                            shrink={true}
                            htmlFor="age-label-placeholder"
                          >
                            Category
                          </InputLabel>
                          <Select
                            value={categoriesObj.selected || ''}
                            onChange={event => {
                              const selectedValue = event.target.value;
                              setCategoriesObj({
                                ...categoriesObj,
                                selected: selectedValue,
                              });
                              setFieldValue('categoryId', selectedValue);
                            }}
                            onBlur={handleBlur}
                            displayEmpty={true}
                            name="categoryId"
                          >
                            {categoriesObj.categories.map(
                              (category: any, index: number) => (
                                <MenuItem value={category.id} key={index}>
                                  {category.name}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                        <ErrorMessage
                          className="error"
                          component="div"
                          name="categoryId"
                        />
                      </Grid>
                    </Grid>
                    {/* Products Checkbox List */}
                    {productsObj.products.length > 0 && (
                      <>
                        <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {filteredProducts.map((data: any, index: number) => (
                            <div style={{ marginBottom: '-10px' }} key={index}>
                              <Checkbox
                                checked={
                                  data.id == productsObj.selected ? true : false
                                }
                                value={productsObj.selected}
                                name="product_id"
                                onChange={event => {
                                  const isChecked = event.target.checked;
                                  if (isChecked) {
                                    setProductsObj({
                                      ...productsObj,
                                      selected: data.id,
                                    });
                                    promoCopy.product_id = data.id;
                                    setFieldValue('product_id', data.id);
                                  } else {
                                    setProductsObj({
                                      ...productsObj,
                                      selected: '',
                                    });
                                  }
                                }}
                              />
                              {`${data.name} $(${data.price})`}
                            </div>
                          ))}
                        </div>

                        <ErrorMessage
                          className="error"
                          component="div"
                          name="product_id"
                        />
                      </>
                    )}
                  </>
                )}
                <TextField
                  error={touched.code && !!errors.code}
                  required={true}
                  className="generic-dialog__field"
                  name="code"
                  inputProps={{
                    type: 'string',
                  }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Code"
                  value={values.code}
                  autoComplete="off"
                />
                <ErrorMessage className="error" component="div" name="code" />
                <div className="generic-dialog__field">
                  This is Pos Discount:
                  <Switch
                    classes={{
                      track: 'custom-colored-switch-track',
                    }}
                    name="isPosDiscount"
                    color="primary"
                    checked={values.isPosDiscount}
                    value={values.isPosDiscount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DateTimePicker
                    required={true}
                    name="startDateTime"
                    ampm={true}
                    disablePast={promoCopy.id ? false : true}
                    className="generic-dialog__field"
                    variant="inline"
                    margin="normal"
                    maxDate={values.endDateTime}
                    maxDateMessage="Start Date cannot be after End Date"
                    label="Start Date Time"
                    value={values.startDateTime}
                    onChange={(value: any) => {
                      setFieldValue('startDateTime', value);
                    }}
                  />
                  <ErrorMessage
                    className="error"
                    component="div"
                    name="startDateTime"
                  />
                  <DateTimePicker
                    required={true}
                    name="endDateTime"
                    ampm={true}
                    disablePast={promoCopy.id ? false : true}
                    className="generic-dialog__field"
                    variant="inline"
                    margin="normal"
                    label="End Date Time"
                    value={values.endDateTime}
                    onChange={(value: any) => {
                      setFieldValue('endDateTime', value);
                    }}
                  />
                  <ErrorMessage
                    className="error"
                    component="div"
                    name="endDateTime"
                  />
                </MuiPickersUtilsProvider>
                <FormControl required={true} className="generic-dialog__field">
                  <InputLabel shrink={true} htmlFor="age-label-placeholder">
                    Type
                  </InputLabel>
                  <Select
                    value={values.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    displayEmpty={true}
                    name="type"
                  >
                    {Object.keys(PromotionType).map(
                      (promotionType: string, index: number) => (
                        <MenuItem value={promotionType} key={index}>
                          {promotionType}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
                <ErrorMessage className="error" component="div" name="type" />
                <TextField
                  error={touched.discountAmount && !!errors.discountAmount}
                  className="generic-dialog__field"
                  name="discountAmount"
                  type="string"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Discount Amount"
                  value={values.discountAmount}
                  autoComplete="off"
                  inputProps={{
                    min: '0',
                    step: 0.01,
                    type: 'number',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
                <ErrorMessage
                  className="error"
                  component="div"
                  name="discountAmount"
                />
                <TextField
                  error={
                    touched.discountPercentage && !!errors.discountPercentage
                  }
                  className="generic-dialog__field"
                  name="discountPercentage"
                  type="number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Discount Percent"
                  value={values.discountPercentage}
                  autoComplete="off"
                  inputProps={{
                    min: '0',
                    step: 0.01,
                    type: 'number',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
                <ErrorMessage
                  className="error"
                  component="div"
                  name="discountPercentage"
                />
                <TextField
                  required={true}
                  error={
                    touched.minPurchaseAmount && !!errors.minPurchaseAmount
                  }
                  className="generic-dialog__field"
                  name="minPurchaseAmount"
                  type="number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Minimum Purchase Amount"
                  value={values.minPurchaseAmount}
                  autoComplete="off"
                  inputProps={{
                    min: 0.0,
                    step: 0.01,
                    type: 'number',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
                <ErrorMessage
                  className="error"
                  component="div"
                  name="minPurchaseAmount"
                />

                <TextField
                  required={false}
                  error={touched.maxUsesPerUser && !!errors.maxUsesPerUser}
                  className="generic-dialog__field"
                  name="maxUsesPerUser"
                  type="number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Maximum Orders Per User"
                  helperText="Number of orders a user can make with this promo code. Leave blank for unlimited orders."
                  value={values.maxUsesPerUser || ''}
                  autoComplete="off"
                  inputProps={{
                    min: 0,
                    step: 1,
                    type: 'number',
                  }}
                />
                <ErrorMessage
                  className="error"
                  component="div"
                  name="maxUsesPerUser"
                />

                {promoCategory === 'PRODUCT' && (
                  <>
                    <TextField
                      required={false}
                      error={touched.productLimit && !!errors.productLimit}
                      className="generic-dialog__field"
                      name="productLimit"
                      type="number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Product Limit per Promo"
                      helperText="Number of products upto which promo applied. Leave blank for unlimited number of products."
                      value={values.productLimit || ''}
                      autoComplete="off"
                      inputProps={{
                        min: 0,
                        step: 1,
                        type: 'number',
                      }}
                    />
                    <ErrorMessage
                      className="error"
                      component="div"
                      name="productLimit"
                    />
                  </>
                )}
                <div className="generic-dialog__field">
                  Active:
                  <Switch
                    classes={{
                      track: 'custom-colored-switch-track',
                    }}
                    name="active"
                    color="primary"
                    checked={values.active}
                    value={values.active}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="generic-dialog__field">
                  Limit Users:
                  <Switch
                    classes={{
                      track: 'custom-colored-switch-track',
                    }}
                    name="limitUsers"
                    color="primary"
                    checked={values.limitUsers}
                    value={values.limitUsers}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="generic-dialog__field">
                  Exclude Alcohol:
                  <Switch
                    classes={{
                      track: 'custom-colored-switch-track',
                    }}
                    name="excludeAlcohol"
                    color="primary"
                    checked={values.excludeAlcohol}
                    value={values.excludeAlcohol}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="button-holder-different-margin">
                  <DialogActions>
                    <Button onClick={close} color="primary" variant="outlined">
                      <div className="icon-title-wrapper">
                        <UndoOutlinedIcon />
                        <span className="icon-title">Cancel</span>
                      </div>
                    </Button>
                    <Button type="submit" color="primary" variant="contained">
                      <div className="icon-title-wrapper">
                        <CheckOutlinedIcon />
                        <span className="icon-title">
                          {promoCopy.id ? 'Update' : 'Add'}
                        </span>
                      </div>
                    </Button>
                  </DialogActions>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PromoDialog;
